<template>
  <match-media v-slot="{ mobile }">
    <v-dialog
      :title="mobile ? 'Запрос на изменения' : ''"
      :visible.sync="localVisible"
      size="extra-large"
      class="owner-edit-pending-dialog"
    >
      <v-form body-scroll-lock-ignore class="owner-edit-pending-dialog__form" @submit.prevent="onSubmit">
        <template v-if="mobile" #header>
          <v-tabs v-model="activeTab" class="owner-edit-pending-dialog__form-tabs" primary>
            <v-tab name="left">
              {{ $options.TABS_LABELS_MAP[editMode ? 'SEND' : 'BEFORE_SENDING'] }}
            </v-tab>
            <v-tab name="right">
              {{ $options.TABS_LABELS_MAP[editMode ? 'EDIT' : 'SEND'] }}
            </v-tab>
          </v-tabs>
        </template>
        <div class="owner-edit-pending-dialog__form-body">
          <call-edit-pending-dialog-form-content-wrapper
            v-for="({ props, params }, index) in formColumns"
            v-show="params.show"
            :key="index"
            v-bind="props"
            class="owner-edit-pending-dialog__form-content-wrapper"
            :class="{ 'owner-edit-pending-dialog__form-content-wrapper--mobile': mobile }"
            :advert-options="advertOptions"
            :agent-options="agentOptions"
            :call-source-options="callSourceOptions"
            @change="setLocalCallData"
            @select-adverts="onAdvertSearch"
          />
        </div>

        <template #footer>
          <v-button class="owner-edit-pending-dialog__form-button" @click.prevent="editMode = !editMode">{{
            editMode ? 'Отменить' : 'Редактировать запрос'
          }}</v-button>
          <v-button
            v-if="editMode"
            :disabled="isFieldsNotChanged"
            class="owner-edit-pending-dialog__form-button"
            primary
            type="submit"
            >{{ isRoleAdmin ? 'Применить изменения' : 'Отправить обновленный запрос' }}
          </v-button>
        </template>
      </v-form>
    </v-dialog>
  </match-media>
</template>

<script>
import VDialog from '@/components/common/VDialog.vue'
import { advertsService, agentsService, ownersService } from '@/services/http'
import { formatAdvertName, formatAgentObject } from '@/utils/formatters'
import { getFirstErrorForFields, isFieldsNotChanged } from '@/utils/common'
import { loadingService } from '@/services/loading'
import VButton from '@/components/common/VButton.vue'
import VForm from '@/components/form/VForm.vue'
import { mapGetters } from 'vuex'
import { MODULE_SESSION } from '@/store/modules/session/session.types'
import VTabs from '@/components/common/VTabs.vue'
import VTab from '@/components/common/VTab.vue'
import { MatchMedia } from 'vue-component-media-queries'
import CallEditPendingDialogFormContentWrapper from '@/components/Calls/EditPendingDialogFormContentWrapper.vue'

const TABS_LABELS_MAP = {
  BEFORE_SENDING: 'До отправки',
  SEND: 'Отправлено',
  EDIT: 'Редактирование'
}
const EMPTY_CALL = {
  advert: undefined,
  role: undefined,
  name: undefined,
  phone: undefined,
  callDate: undefined,
  callSource: undefined,
  agent: undefined
}

export default {
  TABS_LABELS_MAP,
  name: 'CallEditPendingDialog',
  components: {
    CallEditPendingDialogFormContentWrapper,
    VTab,
    VTabs,
    VForm,
    VButton,
    VDialog,
    MatchMedia
  },
  props: {
    visible: { type: Boolean, required: true },
    callData: {
      type: Object,
      default: () => {
        return {}
      }
    },
    draftData: {
      type: Object,
      default: () => {
        return {}
      }
    }
  },
  data() {
    return {
      activeTab: 'right',
      localCallData: {},
      loading: false,
      errors: {},
      advertOptions: [],
      agentOptions: [],
      callSourceOptions: [],
      editMode: false
    }
  },
  inject: ['mediaQueries'],
  computed: {
    isFieldsNotChanged() {
      return isFieldsNotChanged(this.callData, this.localCallData, { isAdmin: this.isRoleAdmin, objectType: 'call' })
    },
    formColumns() {
      return [
        {
          props: {
            title: this.editMode ? TABS_LABELS_MAP.SEND : TABS_LABELS_MAP.BEFORE_SENDING,
            callData: this.editMode ? this.formatCallData(this.draftData) : this.formatCallData(this.callData),
            disableFields: true,
            editMode: false,
            backendErrors: null
          },
          params: {
            show: !this.mediaQueries.mobile || this.isFormType('left')
          }
        },
        {
          props: {
            title: this.editMode ? TABS_LABELS_MAP.EDIT : TABS_LABELS_MAP.SEND,
            callData: this.editMode ? this.localCallData : this.formatCallData(this.draftData),
            disableFields: !this.editMode,
            editMode: this.editMode,
            backendErrors: this.errors
          },
          params: {
            show: !this.mediaQueries.mobile || this.isFormType('right')
          }
        }
      ]
    },
    ...mapGetters({
      isRoleAdmin: `${MODULE_SESSION}/isRoleAdmin`
    }),
    localVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.editMode = false
        this.$emit('update:visible', val)
      }
    }
  },
  watch: {
    editMode: {
      handler() {
        this.localCallData = this.formatCallData(this.draftData)
        this.errors = {}
      }
    },
    loading(val) {
      loadingService.setGlobalLoading(val)
    }
  },
  created() {
    if (this.isRoleAdmin) {
      this.selectAgents()
    }
    this.selectAdverts()
    this.selectCallSources()
  },
  methods: {
    formatCallData(data) {
      if (Object.keys(data).length) {
        const { account, ...restModel } = data
        const {
          profile: { agent },
          ...restField
        } = account
        return {
          ...restModel,
          ...restField,
          agent,
          advert: { ...data?.advert, name: formatAdvertName(data?.advert, !data.advert) }
        }
      }
      return EMPTY_CALL
    },
    onAdvertSearch({ query, loading }) {
      loading(true)
      this.selectAdverts(query).finally(() => {
        loading(false)
      })
    },
    isFormType(type) {
      return this.activeTab === type
    },
    setLocalCallData(newData) {
      this.localCallData = newData
    },
    selectAgents() {
      return agentsService.select().then(agentList => {
        this.agentOptions = agentList.map(agent => formatAgentObject(agent))
      })
    },
    selectAdverts() {
      return advertsService.select().then(advertsList => {
        this.advertOptions = advertsList.map(advert => {
          const { name, roundedPrice } = advert
          return {
            ...advert,
            name: `${name} - ${roundedPrice}`
          }
        })
      })
    },
    selectCallSources() {
      return ownersService.getCallSourcesMap().then(callSourcesList => {
        this.callSourceOptions = callSourcesList
      })
    },
    onSubmit() {
      this.loading = true
      agentsService
        .createCall({ ...this.localCallData, ...{ initialAdvertCall: this.callData.id } })
        .then(newData => {
          this.$emit('submitted', newData)
          this.localVisible = false
        })
        .catch(error => {
          const flatErrors = { ...error.details, ...error.details.profile }
          this.errors = getFirstErrorForFields(flatErrors)
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>
